import React from 'react';

import { atom } from 'jotai';

import { ButtonProps } from '@cfa/react-core';

export interface PathwayModalAtomState {
  bodyContent?: React.ReactNode;
  bodyClassName?: string;
  bodyNoMinHeight?: boolean;
  bodyText?: string;
  bodyTextColor?: string;
  className?: string;
  /**
   * Fully custom footer content rendered inside ScrollModalFooter.
   * When provided, replaces the default primary/secondary button pair.
   * The scroll-shadow behaviour is handled by PathwayModal automatically.
   */
  footerContent?: React.ReactNode;
  /**
   * Optional class name merged onto the footer wrapper (ScrollModalFooter).
   * Use to override default footer padding/spacing on a per-modal basis.
   */
  footerClassName?: string;
  /**
   * Title string rendered at the top of the header. Required.
   * Pair with `headerContent` to add extra elements below the title
   * (e.g. a search bar).
   */
  headerTitle: string;
  headerContent?: React.ReactNode;
  /** When true, removes default horizontal padding from the header wrapper. */
  headerNoPadding?: boolean;
  isDisabled?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  modalSize?: 'sm' | 'md' | 'lg' | 'xl';
  onClose?: () => void;
  overlayClassName?: string;
  overlayStyle?: React.CSSProperties;
  primaryButtonColor?: ButtonProps['color'];
  primaryButtonHandler?: () => void;
  primaryButtonTestId?: string;
  primaryButtonText?: React.ReactNode;
  primaryButtonVariant?: ButtonProps['variant'];
  secondaryButtonColor?: ButtonProps['color'];
  secondaryButtonDisabled?: boolean;
  secondaryButtonHandler?: () => void;
  secondaryButtonLoading?: boolean;
  secondaryButtonTestId?: string;
  secondaryButtonText?: React.ReactNode;
  secondaryButtonVariant?: ButtonProps['variant'];
  showCloseButton?: boolean;
  stepKey?: string;
  /**
   * When true, the modal body fills 90dvh, all height animations are
   * suppressed, and opacity never changes between renders. Use for steps
   * where the body content grows/shrinks (e.g. accordions) and the
   * fixed-height layout is preferable to an animated resize.
   */
  fullHeight?: boolean;
  /**
   * Optional ref that, when provided, will be populated with the rendered
   * modal body's scrollable DOM element. Use this to wire scroll memory or
   * other scroll-dependent behaviour to the real scroller inside
   * PathwayModalRenderer.
   */
  bodyRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * Internal: ID of the composer instance that last opened the modal.
   * Used to prevent closed instances from clobbering an open one.
   */
  _ownerId?: string;
}

export const pathwayModalInitialState: PathwayModalAtomState = {
  isOpen: false,
  headerTitle: '',
};

export const pathwayModalAtom = atom(pathwayModalInitialState);
